/**
 * Код ANZONIX: дерево разбора.
 *
 * Общие типы для разбора (`script.ts`) и пошагового расчёта
 * (`script-steps.ts`). Отдельным файлом, чтобы расчёт по свечам не
 * тянул за собой разбор, а разбор — расчёт.
 */
export class Fail extends Error {
    line;
    column;
    constructor(line, column, message) {
        super(message);
        this.line = line;
        this.column = column;
    }
}
/** Даёт ли выражение «да/нет», а не число: сравнение, «и/или/не», пересечение. */
export function isCondition(expr) {
    if (expr.kind === 'binary')
        return ['<', '>', '<=', '>=', '==', '!=', 'and', 'or'].includes(expr.op);
    if (expr.kind === 'unary')
        return expr.op === 'not';
    if (expr.kind === 'call')
        return ['crossup', 'crossdown', 'cross', 'rising', 'falling', 'isna'].includes(expr.name);
    if (expr.kind === 'session')
        return true;
    return false;
}
/** Даёт ли выражение текст: подпись метки, строка табло. */
export function isText(expr) {
    if (expr.kind === 'text')
        return true;
    if (expr.kind === 'call' && expr.name === 'tostr')
        return true;
    if (expr.kind === 'binary' && expr.op === '+')
        return isText(expr.left) || isText(expr.right);
    if (expr.kind === 'call' && expr.name === 'iff')
        return isText(expr.args[1]) || isText(expr.args[2]);
    return false;
}
/**
 * Считается ли выражение целиком по истории: в нём только цены,
 * настройки, ряды и функции от них. Всё, что зависит от памяти, циклов,
 * функций кода, списков и рисования, считается свеча за свечой.
 */
export function isPure(expr) {
    let known = purity.get(expr);
    if (known === undefined) {
        known = pureOf(expr);
        purity.set(expr, known);
    }
    return known;
}
const purity = new WeakMap();
function pureOf(expr) {
    switch (expr.kind) {
        case 'num':
        case 'session':
        case 'on':
            return true;
        case 'text':
        case 'color':
        case 'ucall':
        case 'lcall':
        case 'draw':
        case 'at':
            return false;
        case 'name':
            return expr.bind === undefined || expr.bind === 'price' || expr.bind === 'input' || expr.bind === 'series';
        case 'call':
            return expr.name !== 'tostr' && expr.args.every(isPure);
        case 'member':
        case 'index':
        case 'unary':
            return isPure(expr.of);
        case 'binary':
            return isPure(expr.left) && isPure(expr.right);
    }
}
/** Обойти все выражения внутри шагов — для поиска чужих рядов и проверок. */
export function walkSteps(steps, visit) {
    for (const step of steps) {
        switch (step.kind) {
            case 'memory':
                visit(step.init);
                break;
            case 'assign':
            case 'do':
                visit(step.expr);
                break;
            case 'if':
                for (const branch of step.branches) {
                    visit(branch.when);
                    walkSteps(branch.body, visit);
                }
                if (step.otherwise)
                    walkSteps(step.otherwise, visit);
                break;
            case 'for':
                visit(step.from);
                visit(step.to);
                if (step.step)
                    visit(step.step);
                walkSteps(step.body, visit);
                break;
            case 'while':
                visit(step.when);
                walkSteps(step.body, visit);
                break;
            case 'return':
                if (step.expr)
                    visit(step.expr);
                break;
            case 'signal':
                if (step.text)
                    visit(step.text);
                break;
            default:
                break;
        }
    }
}
/** Вложенные выражения одного выражения. */
export function childrenOf(expr) {
    switch (expr.kind) {
        case 'call':
        case 'ucall':
        case 'lcall':
            return expr.args;
        case 'draw':
            return expr.text ? [...expr.args, expr.text] : expr.args;
        case 'member':
        case 'index':
        case 'unary':
        case 'on':
            return [expr.of];
        case 'at':
            return [expr.of, expr.back];
        case 'binary':
            return [expr.left, expr.right];
        default:
            return [];
    }
}
//# sourceMappingURL=script-ast.js.map